import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import apiService from '../../services/api';

const RED_FLAG_REASONS = [
    'רקע קליני מורכב',
    'המטופל מראה סימנים פסיכוטיים',
    'ריבוי גורמי טיפול / מטופל במספר מסגרות טיפול',
    'שימוש בחומרים / התמכרות',
    'ניסיון אובדני בעבר',
    'רקע משפחתי או סוציאלי מורכב',
    'חוסר תפקוד ברמה מדאיגה',
    'אחר',
];

const OTHER_REASON = 'אחר';

interface RedFlagModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId: number;
    userId: number;
    isRedFlagged: boolean;
    initialReasons?: string[];
    initialOtherText?: string;
    onSuccess: () => void;
    onRemoveSuccess?: () => void;
}

export const RedFlagModal: React.FC<RedFlagModalProps> = ({
    isOpen,
    onClose,
    patientId,
    userId,
    isRedFlagged,
    initialReasons,
    initialOtherText,
    onSuccess,
    onRemoveSuccess,
}) => {
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const token = localStorage.getItem('token') || '';
    const isOtherSelected = selectedReasons.includes(OTHER_REASON);
    const prevIsOpenRef = useRef(false);

    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            let reasons = initialReasons ?? [];
            // API sometimes returns arrays as JSON-encoded strings
            if (!Array.isArray(reasons)) {
                try { reasons = JSON.parse(reasons as unknown as string); } catch { reasons = []; }
            }
            setSelectedReasons(reasons);
            setAdditionalNotes(initialOtherText ?? '');
            setError(null);
            setShowConfirmRemove(false);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, initialReasons, initialOtherText]);

    const toggleReason = (reason: string) => {
        setSelectedReasons(prev =>
            prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
        );
        setError(null);
    };

    const handleSave = async () => {
        setError(null);
        if (selectedReasons.length === 0) {
            setError('Please select at least one reason');
            return;
        }
        if (isOtherSelected && !additionalNotes.trim()) {
            setError('Additional notes are required when "אחר" is selected');
            return;
        }
        setIsSaving(true);
        try {
            await apiService.redFlagRequest({
                token,
                patient_id: patientId,
                user_id: userId,
                red_flag_reason: selectedReasons,
                red_flag_other: additionalNotes.trim(),
                remove_flag: false,
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save red flag');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveConfirm = async () => {
        setIsRemoving(true);
        try {
            await apiService.redFlagRequest({
                token,
                patient_id: patientId,
                user_id: userId,
                red_flag_reason: [],
                red_flag_other: '',
                remove_flag: true,
            });
            onSuccess();
            onRemoveSuccess?.();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove red flag');
            setShowConfirmRemove(false);
        } finally {
            setIsRemoving(false);
        }
    };

    if (!isOpen) return null;

    if (showConfirmRemove) {
        return (
            <ModalWrapper modalId="RedFlagModal"
                isOpen={isOpen}
                onClose={() => setShowConfirmRemove(false)}
                title="Remove complex patient flag?"
                isLoading={isRemoving}
            >
                <p className="text-sm text-[#366A75] mb-6 text-center">
                    This will remove the red flag indicator from the patient and calendar.
                </p>
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}
                <div className="flex justify-center gap-4">
                    <button
                        onClick={handleRemoveConfirm}
                        disabled={isRemoving}
                        className={POPUP_STYLES.button}
                    >
                        {isRemoving ? 'Removing...' : 'Remove'}
                    </button>
                    <button
                        onClick={() => setShowConfirmRemove(false)}
                        disabled={isRemoving}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title="Complex Patient Flag"
            isLoading={isSaving}
        >
            <div className="space-y-4 mb-6">
                {/* Reasons multi-select list */}
                <div className="space-y-1" dir="rtl">
                    {RED_FLAG_REASONS.map(reason => (
                        <label
                            key={reason}
                            className="flex items-center gap-3 cursor-pointer px-3 py-2 rounded-lg hover:bg-[#D0E7E5] transition-colors"
                        >
                            <input
                                type="checkbox"
                                checked={selectedReasons.includes(reason)}
                                onChange={() => toggleReason(reason)}
                                disabled={isSaving}
                                className="w-4 h-4 accent-[#366A75] flex-shrink-0"
                            />
                            <span className="text-sm text-[#366A75] font-medium">{reason}</span>
                        </label>
                    ))}
                </div>

                {/* Additional notes */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Additional notes:{isOtherSelected && <span className="text-red-500"> *</span>}
                    </label>
                    <textarea
                        value={additionalNotes}
                        onChange={(e) => setAdditionalNotes(e.target.value)}
                        placeholder={
                            isOtherSelected
                                ? 'Enter additional clinical context - mandatory'
                                : 'Enter additional clinical context - optional'
                        }
                        rows={3}
                        className={`${POPUP_STYLES.input} resize-none`}
                        disabled={isSaving}
                    />
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            <div className="flex items-center justify-between">
                {isRedFlagged ? (
                    <button
                        onClick={() => setShowConfirmRemove(true)}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Remove
                    </button>
                ) : (
                    <div />
                )}
                <div className="flex gap-2">
                    <button
                        onClick={handleSave}
                        disabled={
                            isSaving ||
                            selectedReasons.length === 0 ||
                            (isOtherSelected && !additionalNotes.trim())
                        }
                        className={POPUP_STYLES.button}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
